import { Eye, FileText, SquareArrowOutUpRight, Stamp, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Sheet } from '@/components/ui/Sheet'
import { ApiError } from '@/lib/api'
import { refusalMessage } from '@/lib/refusals'
import {
  useIssueDocument,
  useRenderDocument,
  type DocumentRef,
} from '@/features/finance/api/useFinance'
import { useDropInvoice } from '@/features/finance/api/useInvoices'
import { useTrancheCarryOver } from '@/features/finance/api/useReceivables'
import { receivableLabel } from '@/features/finance/allocationLabel'
import { formatMoneyString } from '@/lib/format'
import { InvoicePreviewSheet } from '@/features/finance/components/InvoicePreviewSheet'
import type { CaseDocument } from '@/types/domain'

/* Everything that can be done to one document, on the document's own row.
 *
 * ── Why the actions moved onto the row ──────────────────────────────────────
 *
 * They used to live in a panel beside the table, against whichever row was
 * selected. That panel cost the table roughly a third of its width — a five
 * column list of money was squeezed into half a card while the space next to it
 * held three buttons and a hand-drawn sketch of the document — and it bought
 * one thing: the buttons named a single document, so nobody could press
 * "Issue" against the row their finger landed on.
 *
 * That guarantee is kept here without the panel. Each icon belongs to the row
 * it sits in, which is a stronger statement of WHICH document than a panel
 * floating beside a selection; and the two irreversible actions do not act on
 * the press at all — they open a dialogue that names the document and spells
 * out the consequence in words.
 *
 * ── Icons, and the rule about icons ─────────────────────────────────────────
 *
 * The design rule: a destructive or financial action always carries a text label,
 * and `IconButton`'s own comment repeats it. That rule is about the moment of
 * DECISION, and it is honoured: issuing and discarding are decided in a sheet
 * with a sentence, a named consequence and a labelled button. The icon is the
 * way in, not the act — and it is never unlabelled, since IconButton requires a
 * name that a screen reader reads and a hover shows.
 *
 * Generating a PDF and opening the invoice carry no such weight: one can be
 * repeated all afternoon, the other is navigation.
 *
 * ── What a debit note does not get ──────────────────────────────────────────
 *
 * Preview, open and discard are invoice-only, because that is what the server
 * offers: `/cases/:id/invoices/:invoiceId/preview` and the DELETE beside it
 * have no debit-note counterpart. An icon that could only ever answer 404 is
 * worse than an absent one — the reader would take the failure for a fault in
 * their document rather than in the screen.
 */
export function DocumentRowActions({
  record,
  /** Where "open" goes. Absent on a debit note, which has no page of its own. */
  onOpen,
  /** Whether this reader may issue a document of THIS row's kind
   *  (`mayIssueDocument`). Told by the parent, which reads the session once
   *  for the whole table, so the row needs none of its own. Without it the
   *  Issue icon is absent rather than refused: no state of the paper would
   *  ever let this reader press it. */
  mayIssue,
  /** The tranche an invoice replaces, when it names one — the issue then
   *  carries the money already received on it over to the invoice (S-21), and
   *  the confirmation says how much. */
  trancheId,
}: {
  record: CaseDocument
  onOpen?: () => void
  mayIssue: boolean
  trancheId?: string | null
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const render = useRenderDocument()
  const issue = useIssueDocument()
  const drop = useDropInvoice(record.caseId)

  const [confirmingIssue, setConfirmingIssue] = useState(false)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const [previewing, setPreviewing] = useState(false)

  /* Asked only while the confirmation is open: the receivables read is the
     section's own cache entry, so this is normally no request at all. */
  const carried = useTrancheCarryOver(record.caseId, trancheId, {
    enabled: confirmingIssue && record.kind === 'invoice' && !record.issued,
  })

  const ref: DocumentRef = { caseId: record.caseId, kind: record.kind, id: record.id }
  const isInvoice = record.kind === 'invoice'
  const rendered = record.documentId !== undefined
  const busy = render.isPending || issue.isPending || drop.isPending

  /* A second Issue press is REFUSED rather than swallowed, and it earns its own
     sentence: told only that something conflicted, the reading somebody is left
     with is that nothing happened — which is what makes a person raise a second
     demand for the same money (UX-DR-24). Reachable even though the row's Issue
     is blocked on an issued document, because the refusal comes back when THIS
     screen still believes the record is a draft. */
  const issueRefusal = !issue.isError
    ? undefined
    : issue.error instanceof ApiError && issue.error.status === 409
      ? t('finance.documents.alreadyIssued')
      : refusalMessage(issue.error, t)

  const refusal =
    issueRefusal ??
    (render.isError ? refusalMessage(render.error, t) : undefined) ??
    (drop.isError ? refusalMessage(drop.error, t) : undefined)

  return (
    <>
      {/* `justify-end` so the icons hang off the right edge of the table, where
          a row's actions are looked for, and `whitespace-nowrap` so they never
          wrap into a second line and change the row's height. */}
      <div className="flex items-center justify-end gap-0.5 whitespace-nowrap">
        {isInvoice ? (
          <IconButton
            size="sm"
            icon={Eye}
            label={t('finance.documents.actions.preview')}
            onClick={() => setPreviewing(true)}
          />
        ) : null}

        {/* Not offered on an issued document at all: the archived artifact IS
            the one that was sent, and replacing its bytes afterwards would
            rewrite history under a number a client is already holding. The
            server refuses it regardless; this is the affordance. */}
        {record.issued ? null : (
          <IconButton
            size="sm"
            icon={FileText}
            label={
              render.isPending
                ? t('finance.documents.actions.generating')
                : rendered
                  ? t('finance.documents.actions.regenerate')
                  : t('finance.documents.actions.generate')
            }
            disabled={busy}
            onClick={() => {
              // The previous refusal goes with the previous attempt: a message
              // left standing over a fresh press reads as if this one failed
              // too.
              render.reset()
              render.mutate(ref)
            }}
          />
        )}

        {record.issued || !mayIssue ? null : (
          <IconButton
            size="sm"
            icon={Stamp}
            label={t('finance.documents.actions.issue')}
            /* Off without a current render, because the number is stamped onto
               the archived file and an unrendered record has nothing to stamp
               it onto. The reason is said in the sheet the row's own state
               opens; the server answers the same refusal regardless. */
            disabled={busy || !rendered}
            onClick={() => {
              issue.reset()
              setConfirmingIssue(true)
            }}
          />
        )}

        {isInvoice && onOpen !== undefined ? (
          <IconButton
            size="sm"
            icon={SquareArrowOutUpRight}
            label={t('finance.documents.actions.open')}
            onClick={onOpen}
          />
        ) : null}

        {/* Discarding is a DRAFT's action. An issued invoice keeps no delete
            control, because there is nothing to explain in a button that
            always refuses. */}
        {isInvoice && !record.issued ? (
          <IconButton
            size="sm"
            icon={Trash2}
            label={t('finance.documents.actions.discard')}
            className="hover:bg-negative-soft hover:text-negative"
            disabled={busy}
            onClick={() => {
              drop.reset()
              setConfirmingDiscard(true)
            }}
          />
        ) : null}
      </div>

      {/* The refusal, under the row that earned it rather than in a toast: a
          toast is gone before somebody has finished reading which of the verbs
          failed. Our sentence, never the server's `detail` (AD-22, AD-27). */}
      {refusal === undefined ? null : (
        <p role="alert" className="text-negative mt-1 text-right text-[12px] leading-snug">
          {refusal}
        </p>
      )}

      {/* Issuing is the confirmed act because it is the irreversible one
          (UX-DR-24, AD-24): it mints the number from the entity's scheme,
          freezes the document and creates the receivable. */}
      <Sheet
        open={confirmingIssue}
        onClose={() => setConfirmingIssue(false)}
        title={t('finance.documents.issueConfirm.title')}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              size="lg"
              disabled={busy}
              onClick={() => setConfirmingIssue(false)}
              className="sm:w-auto"
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              size="lg"
              disabled={busy}
              onClick={() => {
                setConfirmingIssue(false)
                issue.mutate(ref)
              }}
              className="sm:w-auto"
            >
              {t('finance.documents.issueConfirm.confirm')}
            </Button>
          </div>
        }
      >
        <p className="text-text-body text-[13px] leading-snug">
          {t('finance.documents.issueConfirm.body')}
        </p>
        {carried ? (
          <p className="text-text-body mt-2 text-[13px] leading-snug">
            {t('finance.receivables.carryOver', {
              tranche: receivableLabel(t, carried),
              amount: formatMoneyString(carried.paid.amount, carried.paid.currency),
            })}
          </p>
        ) : null}
      </Sheet>

      {/* Discarding is confirmed for the opposite reason to issuing: not
          because it binds anybody, but because what it removes cannot be got
          back and the charges go with it. */}
      <Sheet
        open={confirmingDiscard}
        onClose={() => setConfirmingDiscard(false)}
        title={t('finance.documents.discardConfirm.title')}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              size="lg"
              disabled={busy}
              onClick={() => setConfirmingDiscard(false)}
              className="sm:w-auto"
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="danger"
              size="lg"
              disabled={busy}
              onClick={() => {
                setConfirmingDiscard(false)
                drop.mutate(record.id, {
                  /* Navigation belongs to the caller that HAS a page open on
                     this invoice; from a table there is nowhere to go. The
                     callback is here rather than in the hook because the hook
                     is shared by both surfaces and only one of them is
                     standing on the record it just removed. */
                  onSuccess: () => {
                    if (window.location.pathname.includes(`/invoices/${record.id}`)) {
                      void navigate(`/cases/${record.caseId}?section=documents`)
                    }
                  },
                })
              }}
              className="sm:w-auto"
            >
              {t('finance.documents.discardConfirm.confirm')}
            </Button>
          </div>
        }
      >
        <p className="text-text-body text-[13px] leading-snug">
          {t('finance.documents.discardConfirm.body')}
        </p>
      </Sheet>

      {isInvoice ? (
        <InvoicePreviewSheet
          open={previewing}
          onClose={() => setPreviewing(false)}
          caseId={record.caseId}
          invoiceId={record.id}
        />
      ) : null}
    </>
  )
}
